import * as React from 'react';
import { useRouteContext } from '@tanstack/react-router';
import {
  ApiErrorCode,
  ApiErrorResponseSchema,
  buildOrderDocumentFilename,
  offers,
  ORDER_DOCUMENT_UNPRICED_COUNT_HEADER,
} from '@ooh/contracts';
import type { RouterContext } from '../../../routes/__root';
import { API_BASE } from '../../../lib/api-base';

/**
 * Structured error thrown by `fetchOrderDocumentBlob` on a non-2xx response.
 * `errorCode` is `undefined` when the response body doesn't parse as a
 * structured `ApiErrorResponseSchema` (network failure, legacy/malformed body).
 */
export interface OrderDocumentDownloadError extends Error {
  statusCode: number;
  errorCode: ApiErrorCode | undefined;
}

export function isOrderDocumentDownloadError(error: unknown): error is OrderDocumentDownloadError {
  return (
    error instanceof Error && typeof (error as { statusCode?: unknown }).statusCode === 'number'
  );
}

/**
 * Builds the GET /offers/:offerId/order-document URL. Unlike the import
 * endpoints, `offers.getOrderDocument.path` carries no `/api` prefix, so
 * `API_BASE` is used unmodified — mirrors `endpointBaseUrl`'s non-`/api`
 * branch in `lib/auth-client.ts`.
 */
export function buildOrderDocumentDownloadUrl(offerId: string): string {
  return `${API_BASE}${offers.getOrderDocument.path.replace(':offerId', offerId)}`;
}

/**
 * `Zamowienie_<campaignNumber>.docx` whenever the offer carries a number,
 * `Zamowienie_PROJEKT_oferta-<offerId>.docx` only for a legacy offer that
 * carries none — delegates to the shared `@ooh/contracts` formula so this
 * never diverges from the API's own `Content-Disposition` filename, computed
 * client-side from data already on hand rather than parsing the response
 * header.
 *
 * Keyed on `campaignNumber` alone, not on any confirmation record: every
 * offer is numbered from creation onward (offer-draft-numbering), and the
 * document SHALL print that number "regardless of whether that offer has
 * ever been confirmed" (offer-order-document / "Order document generation
 * endpoint"), with the draft marking surviving only as the fallback for a
 * number-less legacy offer.
 */
export function buildOrderDocumentFileName(offer: {
  campaignNumber: string | null;
  id: number;
}): string {
  return buildOrderDocumentFilename(offer.campaignNumber, offer.id);
}

/**
 * Extracts the structured `errorCode`/`message` fields from a failed response
 * body, if present. Returns an empty object on any parse failure — callers
 * fall back to a generic HTTP-status message.
 */
async function tryExtractApiError(
  response: Response,
): Promise<{ errorCode?: ApiErrorCode; message?: string }> {
  try {
    const body: unknown = await response.json();
    const parsed = ApiErrorResponseSchema.safeParse(body);
    return parsed.success ? { errorCode: parsed.data.errorCode, message: parsed.data.message } : {};
  } catch {
    return {};
  }
}

/** Success result: the blob to save, plus how many accepted-but-unpriced
 * lines the render reported via the X-Order-Document-Unpriced-Count header
 * (bug fix, offer-order-document-export) — 0 when every line is priced. */
export interface OrderDocumentBlobResult {
  blob: Blob;
  unpricedCount: number;
}

/**
 * Fetches the order-document blob. Throws a structured
 * `OrderDocumentDownloadError` on a non-2xx response — the caller maps
 * `errorCode`/`statusCode` to a translated toast.
 *
 * MUST check `response.ok` (error responses on this route are JSON on an
 * otherwise-binary success path) BEFORE calling `response.blob()` — otherwise
 * a structured 409/422 JSON error body gets saved to disk as a corrupt
 * `.docx` file.
 */
export async function fetchOrderDocumentBlob(
  url: string,
  accessToken: string | null,
): Promise<OrderDocumentBlobResult> {
  // eslint-disable-next-line no-restricted-globals -- file download requires raw fetch; callEndpoint does not support binary/blob responses. Precedent: downloadFile in components/screens-provider.tsx (documented exception, apps/web/AGENTS.md).
  const response = await fetch(url, {
    credentials: 'include',
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    signal: AbortSignal.timeout(30_000),
  });
  if (!response.ok) {
    const { errorCode, message } = await tryExtractApiError(response);
    const error: OrderDocumentDownloadError = Object.assign(
      new Error(message ?? `HTTP ${String(response.status)}`),
      { statusCode: response.status, errorCode },
    );
    throw error;
  }
  const unpricedCount = Number(response.headers.get(ORDER_DOCUMENT_UNPRICED_COUNT_HEADER) ?? 0);
  const blob = await response.blob();
  return { blob, unpricedCount };
}

/** Downloads the order document via an offscreen `<a download>`, following the
 * downloadFile pattern (components/screens-provider.tsx).
 * Resolves with the unpriced-line count so the caller can show a warning
 * toast when it is non-zero (bug fix, offer-order-document-export). */
export function useDownloadOrderDocument() {
  const { auth } = useRouteContext({ from: '__root__' }) as RouterContext;
  const [isDownloading, setIsDownloading] = React.useState(false);

  async function download(offerId: string, fileName: string): Promise<number> {
    setIsDownloading(true);
    const a = document.createElement('a');
    a.download = fileName;
    document.body.appendChild(a);
    try {
      const { blob, unpricedCount } = await fetchOrderDocumentBlob(
        buildOrderDocumentDownloadUrl(offerId),
        auth.accessToken,
      );
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.click();
      URL.revokeObjectURL(objectUrl);
      return unpricedCount;
    } finally {
      document.body.removeChild(a);
      setIsDownloading(false);
    }
  }

  return { download, isDownloading };
}
